import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { listCasinos } from "@/data/casinos";
import { paymentMethods } from "@/data/payments";
import { providers } from "@/data/providers";
import { guides } from "@/data/guides";
import {
  casinoReviewPath,
  guidePath,
  paymentPath,
  providerPath,
  slotPath,
} from "@/lib/format";
import { PriorityPartner } from "./PriorityPartner";

type Group = {
  title: string;
  items: { label: string; href: string }[];
};

type Props = {
  /** Determines which clusters to surface. */
  context:
    | "casino"
    | "casino-list"
    | "licensed"
    | "unlicensed"
    | "spelpaus"
    | "payment"
    | "bonus"
    | "slot"
    | "provider"
    | "guide"
    | "guides-hub"
    | "responsible"
    | "license"
    | "betting"
    | "esports"
    | "homepage"
    | "static";
  /** Slugs to exclude (e.g. the current casino's own slug). */
  exclude?: string[];
  className?: string;
};

export function InternalLinks({ context, exclude = [], className = "" }: Props) {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const groups = buildGroups(context, exclude, lang, prefix, isSv, t);
  const showBetinia = !exclude.includes("betinia");

  if (groups.length === 0 && !showBetinia) return null;

  return (
    <section
      aria-label={isSv ? "Relaterade länkar" : "Related links"}
      className={`mt-12 rounded-2xl border border-ink-100 bg-white p-6 shadow-card md:p-8 ${className}`}
    >
      <header className="section-head">
        <div>
          <span className="eyebrow">
            {isSv ? "Utforska mer" : "Explore more"}
          </span>
          <h2 className="mt-2 text-2xl font-bold">
            {isSv ? "Relaterat innehåll" : "Related content"}
          </h2>
        </div>
      </header>
      {showBetinia && (
        <div className="mt-5">
          <PriorityPartner
            variant="compact"
            placement="internal-links"
            source={`internal-links-${context}`}
          />
        </div>
      )}
      <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {groups.map((g) => (
          <div key={g.title}>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-500">
              {g.title}
            </h3>
            <ul className="mt-3 space-y-2 text-sm">
              {g.items.map((item) => (
                <li key={item.href} className="flex items-start gap-2">
                  <span aria-hidden className="mt-1 text-brand-500">
                    →
                  </span>
                  <Link to={item.href} className="text-ink-800 no-underline hover:text-brand-700">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function buildGroups(
  context: Props["context"],
  exclude: string[],
  _lang: "sv" | "en",
  prefix: string,
  isSv: boolean,
  _t: (k: never) => string,
): Group[] {
  const lang = _lang;
  const path = (sv: string, en: string) => `${prefix}/${isSv ? sv : en}`;

  const popularCasinos = (filter?: Parameters<typeof listCasinos>[0]) =>
    listCasinos(filter)
      .filter((c) => !exclude.includes(c.slug))
      .sort(
        (a, b) =>
          Number(b.priority_brand) - Number(a.priority_brand) ||
          b.overall_rating - a.overall_rating,
      )
      .slice(0, 5)
      .map((c) => ({
        label: c.name,
        href: casinoReviewPath(prefix, lang, c.slug),
      }));

  const popularPayments = paymentMethods
    .slice(0, 6)
    .map((p) => ({
      label: isSv ? `Casino med ${p.name}` : `Casinos with ${p.name}`,
      href: paymentPath(prefix, lang, isSv ? p.slug_sv : p.slug_en),
    }));

  const popularGuides = guides
    .slice(0, 6)
    .map((g) => ({
      label: isSv ? g.title_sv : g.title_en,
      href: guidePath(prefix, lang, isSv ? g.slug_sv : g.slug_en),
    }));

  const popularProviders = providers
    .slice(0, 6)
    .map((p) => ({
      label: p.name,
      href: providerPath(prefix, lang, p.slug),
    }));

  const responsibleLinks = [
    {
      label: isSv ? "Ansvarsfullt spelande" : "Responsible gambling",
      href: path("ansvarsfullt-spelande", "responsible-gambling"),
    },
    { label: "Spelpaus.se", href: `${prefix}/spelpaus` },
    { label: "Stödlinjen 020-81 91 00", href: `${prefix}/stodlinjen` },
    {
      label: "Spelinspektionen",
      href: path("spelinspektionen", "swedish-gambling-authority"),
    },
    {
      label: isSv ? "Under granskning" : "Under review",
      href: path("svartlistade-casinon", "blacklisted-casinos"),
    },
  ];

  const licenseLinks = [
    {
      label: isSv ? "Svensk spellicens" : "Swedish license",
      href: path("licenser/svensk-licens", "licenses/swedish-license"),
    },
    {
      label: isSv ? "MGA-licens (Malta)" : "MGA license (Malta)",
      href: path("licenser/mga", "licenses/mga"),
    },
    {
      label: isSv ? "Curaçao-licens" : "Curaçao license",
      href: path("licenser/curacao", "licenses/curacao"),
    },
    {
      label: isSv ? "Estnisk licens" : "Estonian license",
      href: path("licenser/estland", "licenses/estonia"),
    },
    {
      label: isSv ? "UKGC-licens" : "UKGC license",
      href: path("licenser/ukgc", "licenses/ukgc"),
    },
  ];

  const popularSlots = [
    "starburst",
    "book-of-dead",
    "sweet-bonanza",
    "crazy-time",
    "lightning-roulette",
    "reactoonz",
  ].map((slug) => ({
    label: slug
      .split("-")
      .map((s) => s[0].toUpperCase() + s.slice(1))
      .join(" "),
    href: slotPath(prefix, lang, slug),
  }));

  const casinoLists = [
    {
      label: isSv ? "Casino med svensk licens" : "Casinos with Swedish license",
      href: path("casino-med-svensk-licens", "casinos-with-swedish-license"),
    },
    {
      label: isSv ? "Casino utan svensk licens" : "Casinos without Swedish license",
      href: path("casino-utan-svensk-licens", "casinos-without-swedish-license"),
    },
    {
      label: isSv ? "Casino utan Spelpaus" : "Casinos without Spelpaus",
      href: path("casino-utan-spelpaus", "casinos-without-spelpaus"),
    },
    {
      label: isSv ? "Nya casinon" : "New casinos",
      href: path("nya-casinon", "new-casinos"),
    },
    {
      label: isSv ? "Live casino" : "Live casino",
      href: `${prefix}/live-casino`,
    },
  ];

  const bonusLinks = [
    { label: isSv ? "Casino bonus" : "Casino bonus", href: `${prefix}/casino-bonus` },
    { label: "Free spins", href: `${prefix}/free-spins` },
    {
      label: isSv ? "Omsättningskrav" : "Wagering requirements",
      href: path("guider/omsattningskrav", "guides/wagering-requirements"),
    },
  ];

  const groupCasinoLists: Group = {
    title: isSv ? "Populära listor" : "Popular lists",
    items: casinoLists,
  };
  const groupPayments: Group = {
    title: isSv ? "Betalningsmetoder" : "Payment methods",
    items: popularPayments,
  };
  const groupGuides: Group = {
    title: isSv ? "Guider att läsa" : "Guides to read",
    items: popularGuides,
  };
  const groupResponsible: Group = {
    title: isSv ? "Ansvar & skydd" : "Safety & support",
    items: responsibleLinks,
  };
  const groupLicenses: Group = {
    title: isSv ? "Spellicenser" : "Licenses",
    items: licenseLinks,
  };
  const groupProviders: Group = {
    title: isSv ? "Speltillverkare" : "Software providers",
    items: popularProviders,
  };
  const groupSlots: Group = {
    title: isSv ? "Populära spelautomater" : "Popular slots",
    items: popularSlots,
  };
  const groupBonus: Group = {
    title: isSv ? "Bonusar" : "Bonuses",
    items: bonusLinks,
  };
  const groupTopCasinos = (filter?: Parameters<typeof listCasinos>[0]): Group => ({
    title: isSv ? "Topp casinon" : "Top casinos",
    items: popularCasinos(filter),
  });

  switch (context) {
    case "casino":
      return [
        groupTopCasinos(),
        groupCasinoLists,
        groupPayments,
        groupResponsible,
      ];
    case "casino-list":
    case "homepage":
      return [groupPayments, groupGuides, groupProviders, groupResponsible];
    case "licensed":
      return [
        groupTopCasinos({ swedishLicense: true }),
        groupPayments,
        groupGuides,
        groupResponsible,
      ];
    case "unlicensed":
    case "spelpaus":
      return [
        groupTopCasinos({ noSwedishLicense: true }),
        groupResponsible,
        groupLicenses,
        groupGuides,
      ];
    case "payment":
      return [
        groupTopCasinos(),
        groupPayments,
        groupCasinoLists,
        groupGuides,
      ];
    case "bonus":
      return [
        groupTopCasinos({ swedishLicense: true }),
        groupBonus,
        groupGuides,
        groupResponsible,
      ];
    case "slot":
    case "provider":
      return [
        groupTopCasinos(),
        groupSlots,
        groupProviders,
        groupGuides,
      ];
    case "guide":
    case "guides-hub":
      return [groupGuides, groupCasinoLists, groupPayments, groupResponsible];
    case "responsible":
      return [groupResponsible, groupLicenses, groupCasinoLists, groupGuides];
    case "license":
      return [groupLicenses, groupCasinoLists, groupGuides, groupResponsible];
    case "betting":
    case "esports":
      return [
        groupTopCasinos(),
        groupGuides,
        groupBonus,
        groupResponsible,
      ];
    case "static":
    default:
      return [groupCasinoLists, groupPayments, groupGuides, groupResponsible];
  }
}
